import { useCallback, useContext, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { AsyncActionStatus } from '../../../../actions/common.actionTypes';
import {
	getCurrentConversation,
	getCurrentConversationId,
} from '../../../../reducers/aiAssistant.selectors';
import {
	AIAssistantDisplayMode,
	aiAssistantContext,
} from '../../../../state/AIAssistantContextProvider';
import { aiAssistantEditorContext } from '../../../../state/AIAssistantEditorProvider';
import { smoothScrollIntoView } from '../../../../utils/scroll';
import { ActiveView } from '../constants';
import type { PodcastMessageContext } from '../podcastTypes';

// Short delay to ensure the DOM has settled before scrolling.
// We use setTimeout over requestAnimationFrame because requestAnimationFrame
// pauses in background tabs, which would miss scroll updates during streaming.
export const SCROLL_DELAY_MS = 50;

/**
 * Central scroll behavior hook for the AI assistant chat.
 *
 * Current cases:
 * 1. User sends a message and the AI streams a response — chat stays at the bottom
 * 2. User opens the AI assistant dialog — chat scrolls to the latest message
 * 3. User opens/selects a document or podcast artifact (including a still-generating
 *    podcast, whose loading pane occupies the editor slot) — the chat panel scrolls to its
 *    card once, then the target is cleared so later conversation updates don't re-scroll to it
 * 4. User closes the editor — the chat scrolls back to the closed artifact's re-expanded
 *    card, compensating the layout reflow of every card expanding again
 * 5. User navigates to a conversation with an image scroll target — chat scrolls to that message
 * 6. A new artifact card (document/podcast) lands in the conversation — chat scrolls to the
 *    bottom. Case 1 only covers cards that render while the request is in flight; a card
 *    message landing after that got no scroll, which Chrome masked via scroll anchoring but
 *    Safari (no anchoring) did not.
 */
const useAIAssistantAutoScroll = (): void => {
	const { dialogContentRef, isAssistantOpen, displayMode, activeView } =
		useContext(aiAssistantContext);
	const {
		isTextEditorOpen,
		editorCloseScrollTarget,
		clearEditorCloseScrollTarget,
		activeDocumentCardRef,
		activeImageRef,
		imageScrollTarget,
		clearImageScrollTarget,
	} = useContext(aiAssistantEditorContext);
	const currentConversation = useSelector(getCurrentConversation);
	const currentConversationId = useSelector(getCurrentConversationId);
	const isPageMode = displayMode === AIAssistantDisplayMode.PAGE;

	// This can be executed by two useEffects but it is safe
	// because scrollTo for the same position is idempotent.
	const scrollToBottom = useCallback((): ReturnType<typeof setTimeout> => {
		return setTimeout(() => {
			dialogContentRef?.current?.scrollTo({
				top: dialogContentRef.current.scrollHeight,
				behavior: 'smooth',
			});
		}, SCROLL_DELAY_MS);
	}, [dialogContentRef]);

	// 1. User sends a message and the AI streams a response — chat stays at the bottom
	useEffect(() => {
		if (currentConversation?.status !== AsyncActionStatus.LOADING) return;
		const timerId = scrollToBottom();
		return () => clearTimeout(timerId);
	}, [currentConversation, scrollToBottom]);

	// 2. User opens the AI assistant dialog — chat scrolls to the latest message
	useEffect(() => {
		if (!isAssistantOpen) return;
		const timerId = scrollToBottom();
		return () => clearTimeout(timerId);
	}, [isAssistantOpen, scrollToBottom]);

	// 6. A new artifact card lands in the conversation — chat scrolls to the bottom
	const messages = currentConversation?.data?.messages ?? [];
	const artifactCardCount = messages.filter((message) => {
		const context = message.context as PodcastMessageContext | undefined;
		return !!context?.generatedDocumentId || !!context?.generatedPodcastId;
	}).length;
	const hasMessages = messages.length > 0;
	const lastSeenCardsRef = useRef<{
		conversationId: string | null;
		count: number;
		hadMessages: boolean;
	} | null>(null);
	useEffect(() => {
		const lastSeen = lastSeenCardsRef.current;
		lastSeenCardsRef.current = {
			conversationId: currentConversationId,
			count: artifactCardCount,
			hadMessages: hasMessages,
		};
		// Only a card-count increase within the same, already-populated conversation is a
		// newly created card. Switching conversations re-baselines, and a jump from zero
		// messages is the async load of an opened conversation (a genuinely new card is
		// always preceded by at least the user's prompt message).
		if (!lastSeen || lastSeen.conversationId !== currentConversationId) return;
		if (!lastSeen.hadMessages) return;
		if (artifactCardCount <= lastSeen.count) return;
		const timerId = scrollToBottom();
		return () => clearTimeout(timerId);
	}, [currentConversationId, artifactCardCount, hasMessages, scrollToBottom]);

	// 3, 4 & 5. User opens/selects an artifact, closes the editor, or navigates to an image
	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (!isPageMode) return;

		const hasDocumentTarget = !!editorCloseScrollTarget;
		const hasImageTarget = !!imageScrollTarget;
		if (!hasDocumentTarget && !hasImageTarget) return;

		const timerId = setTimeout(() => {
			if (editorCloseScrollTarget) {
				// A generating podcast occupies the editor slot with the loading pane instead of
				// the editor, so its open-phase target is ready in that view too (WF-64).
				const isPodcastLoadingView =
					!!editorCloseScrollTarget.podcastId && activeView === ActiveView.PODCAST_LOADING;
				// Each phase fires only against its own layout; cleared after the first successful
				// scroll so later conversation updates can't re-consume it.
				const editorStateReady =
					editorCloseScrollTarget.phase === 'close'
						? !isTextEditorOpen
						: isTextEditorOpen || isPodcastLoadingView;
				if (editorStateReady && activeDocumentCardRef?.current) {
					smoothScrollIntoView(activeDocumentCardRef.current);
					clearEditorCloseScrollTarget();
				}
			} else if (hasImageTarget) {
				if (activeImageRef?.current) {
					smoothScrollIntoView(activeImageRef.current);
				}
				if (activeImageRef?.current) {
					clearImageScrollTarget();
				}
			}
		}, SCROLL_DELAY_MS);

		return () => clearTimeout(timerId);
	}, [
		isTextEditorOpen,
		isPageMode,
		activeView,
		editorCloseScrollTarget,
		activeDocumentCardRef,
		clearEditorCloseScrollTarget,
		imageScrollTarget,
		activeImageRef,
		clearImageScrollTarget,
		// Retries the ref-based scroll while the card renders; safe now that the
		// target clears after the first successful scroll.
		currentConversation,
	]);
};

export default useAIAssistantAutoScroll;
